import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

import { Box } from '@base-app';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';

import { EditContentProps, formatDate } from './EditContent.types';
import { SequenceGoal } from '../SequenceGoal/SequenceGoal.types';
import { StyledContent, StyledDate, StyledNote, StyledTitle } from './EditContent.styles';

import EditContentTable from './EditContentTable/EditContentTable';
import { Sizes, Weights } from '@/common/types';
import { NumberText } from '@/common/components';

const EditContent: FC<EditContentProps> = ({ data, onSubmit }) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const [totalLifetime, setTotalLifetime] = useState<null | number>(null);

  const getTotalLifetime = (): void => {
    let total: number | null = null;

    data.goals &&
      data?.goals.forEach(({ target }) => {
        if (typeof target === 'number') {
          total += target as any;
        }
      });

    setTotalLifetime(total);
  };

  const handleOnSubmit = (data: SequenceGoal[]): void => {
    onSubmit(data);
  };

  useEffect(() => {
    getTotalLifetime();
  }, [data]);

  return (
    <Box>
      <StyledContent>
        <StyledTitle>{t('edit_goals.lifetime_sequencing_goal')}</StyledTitle>
        <NumberText size={Sizes.MEDIUM} testId="edit-content-lifetime" weight={Weights.EXTRA_BOLD}>
          {totalLifetime ?? t('edit_goals.not_yet_defined')}
        </NumberText>
        <StyledNote>
          {totalLifetime && t('edit_goals.info_message', { total: Number(totalLifetime).toLocaleString('en') })}
        </StyledNote>
      </StyledContent>
      <StyledContent>
        <StyledTitle>{t('edit_goals.lifetime_time_range')}</StyledTitle>
        <StyledDate data-testid={'edit-content-date'}>
          {data.startDate && data.endDate
            ? `${format(new Date(data.startDate), formatDate)} - ${format(new Date(data.endDate), formatDate)}`
            : `${t('edit_goals.format_date')} - ${t('edit_goals.format_date')}`}
        </StyledDate>
      </StyledContent>
      <StyledContent>
        <EditContentTable goals={data.goals} onSubmit={handleOnSubmit} />
      </StyledContent>
    </Box>
  );
};

export default EditContent;
